(() => {
  'use strict';

  const instances = new WeakMap();
  const selector = '[data-n-reviews]';

  const sectionsWithin = (scope) => {
    const sections = Array.from(scope.querySelectorAll(selector));
    if (scope.matches?.(selector)) sections.unshift(scope);
    return sections;
  };

  const createCarousel = (section) => {
    const track = section.querySelector('[data-n-review-track]');
    const cards = Array.from(section.querySelectorAll('[data-n-review]'));
    if (!track || !cards.length) return null;

    const previous = section.querySelector('[data-n-review-prev]');
    const next = section.querySelector('[data-n-review-next]');
    const dots = Array.from(section.querySelectorAll('[data-n-review-dot]'));
    const status = section.querySelector('[data-n-review-status]');
    const controls = section.querySelector('[data-n-review-controls]');
    const mobile = window.matchMedia('(max-width: 749px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const listeners = new AbortController();
    let activeIndex = 0;
    let frame = 0;

    const positions = () => {
      const trackLeft = track.getBoundingClientRect().left + track.clientLeft;
      const padding = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
      const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
      return cards.map((card) => Math.min(maximum, Math.max(0,
        track.scrollLeft + card.getBoundingClientRect().left - trackLeft - padding
      )));
    };

    const nearestIndex = () => {
      if (!mobile.matches) return 0;
      const targets = positions();
      let nearest = 0;
      targets.forEach((left, index) => {
        if (Math.abs(track.scrollLeft - left) < Math.abs(track.scrollLeft - targets[nearest])) {
          nearest = index;
        }
      });
      return nearest;
    };

    const update = () => {
      frame = 0;
      activeIndex = nearestIndex();
      const canScroll = mobile.matches && track.scrollWidth > track.clientWidth + 1;
      if (controls) controls.hidden = !canScroll;
      track.tabIndex = mobile.matches ? 0 : -1;
      if (previous) previous.disabled = !canScroll || activeIndex === 0;
      if (next) next.disabled = !canScroll || activeIndex === cards.length - 1;
      dots.forEach((dot) => {
        const index = Number(dot.dataset.nReviewDot);
        dot.disabled = !mobile.matches;
        if (index === activeIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      const label = `${activeIndex + 1} de ${cards.length}`;
      if (status && status.textContent !== label) status.textContent = label;
    };

    const scheduleUpdate = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    const goTo = (index) => {
      if (!mobile.matches || !Number.isInteger(index)) return;
      const target = Math.min(cards.length - 1, Math.max(0, index));
      track.scrollTo({
        left: positions()[target],
        behavior: reducedMotion.matches ? 'instant' : 'smooth'
      });
      scheduleUpdate();
    };

    previous?.addEventListener('click', () => goTo(nearestIndex() - 1), { signal: listeners.signal });
    next?.addEventListener('click', () => goTo(nearestIndex() + 1), { signal: listeners.signal });
    dots.forEach((dot) => dot.addEventListener('click', () => {
      goTo(Number(dot.dataset.nReviewDot));
    }, { signal: listeners.signal }));

    track.addEventListener('scroll', scheduleUpdate, { passive: true, signal: listeners.signal });
    track.addEventListener('keydown', (event) => {
      if (event.target !== track || !mobile.matches || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const current = nearestIndex();
      const targets = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: cards.length - 1 };
      if (!Object.prototype.hasOwnProperty.call(targets, event.key)) return;
      event.preventDefault();
      goTo(targets[event.key]);
    }, { signal: listeners.signal });

    const onBreakpointChange = () => {
      if (!mobile.matches) track.scrollTo({ left: 0, behavior: 'instant' });
      scheduleUpdate();
    };
    mobile.addEventListener('change', onBreakpointChange);
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(track);
    cards.forEach((card) => observer.observe(card));
    update();

    return () => {
      listeners.abort();
      if (controls) controls.hidden = true;
      mobile.removeEventListener('change', onBreakpointChange);
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  };

  const initialize = (scope = document) => {
    sectionsWithin(scope).forEach((section) => {
      if (instances.has(section)) return;
      const destroy = createCarousel(section);
      if (destroy) instances.set(section, destroy);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    sectionsWithin(event.target).forEach((section) => {
      instances.get(section)?.();
      instances.delete(section);
    });
  });
})();

// HOME back-to-top links keep their native anchor and keyboard behavior.
(() => {
  const update = () => {
    document.querySelectorAll('[data-n-back-top]').forEach(link => {
      link.hidden = window.scrollY < window.innerHeight;
    });
  };
  let queued = false;
  window.addEventListener('scroll', () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; update(); });
  }, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  document.addEventListener('shopify:section:load', update);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', update, { once: true });
  else update();
})();
